import STATE_OFFICE_MAP from './stateOfficeMap';

const NWS_USER_AGENT = 'RecoverCrew/1.0';
const NWS_BASE_URL = 'https://api.weather.gov/offices';

/**
 * Fetches weather stories for a given NWS office.
 * @param {string} officeId - NWS office identifier (e.g., "LOX")
 * @returns {Promise<object>} - Raw NWS API response
 */
export async function fetchWeatherStories(officeId) {
  const url = `${NWS_BASE_URL}/${officeId}/weatherstories`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'User-Agent': NWS_USER_AGENT,
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`NWS API returned ${response.status}`);
  }

  return response.json();
}

/**
 * Selects the top-priority weather story from a list.
 * Priority: story with `priority === true` wins; otherwise lowest `order` value.
 * @param {Array} stories - Array of weather story objects from `@graph`
 * @returns {object|null} - The selected story, or null if array is empty/invalid
 */
export function selectTopStory(stories) {
  if (!Array.isArray(stories) || stories.length === 0) {
    return null;
  }

  // First, look for a story with priority === true
  const priorityStory = stories.find(s => s.priority === true);
  if (priorityStory) {
    return priorityStory;
  }

  // Otherwise, pick the story with the lowest order value
  return stories.reduce((best, current) => {
    const bestOrder = best.order ?? Infinity;
    const currentOrder = current.order ?? Infinity;
    return currentOrder < bestOrder ? current : best;
  }, stories[0]);
}

/**
 * Returns all offices for a state as an array, or null if unmapped.
 * @param {string} fipsCode - Two-digit state FIPS code
 * @returns {string[]|null} - Array of office IDs or null
 */
export function getOfficesForState(fipsCode) {
  return STATE_OFFICE_MAP[fipsCode] || null;
}

/**
 * Resolves a FIPS code to the primary NWS office ID (first in the array).
 * Kept for backward compatibility.
 * @param {string} fipsCode - Two-digit state FIPS code
 * @returns {string|null} - Office ID or null if unmapped
 */
export function getOfficeForState(fipsCode) {
  const offices = STATE_OFFICE_MAP[fipsCode];
  if (!offices || offices.length === 0) return null;
  return offices[0];
}

/**
 * Fetches the top weather story by trying multiple offices for a state.
 * Returns the first office's top story that has content, or null.
 * @param {string} fipsCode - Two-digit state FIPS code
 * @returns {Promise<object|null>} - The top story, or null if none found
 */
export async function fetchStateTopStory(fipsCode) {
  const offices = STATE_OFFICE_MAP[fipsCode];
  if (!offices || offices.length === 0) return null;

  for (const officeId of offices) {
    try {
      const response = await fetchWeatherStories(officeId);
      const stories = response.stories || response['@graph'] || [];
      const topStory = selectTopStory(stories);
      if (topStory) return topStory;
    } catch (err) {
      // This office failed, try next
      continue;
    }
  }

  return null; // No office had stories
}

export { STATE_OFFICE_MAP };
